import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { ContextMenuState, ContextMenuItem } from '../interfaces/context-menu.interface';

/**
 * Context Menu Service
 *
 * Manages the state and positioning of context menus in the Smart Grid
 * This is a global service that can manage multiple context menus
 */
@Injectable({
  providedIn: 'root',
})
export class ContextMenuService<T = any> {
  /**
   * Context menu state stream
   */
  private stateSubject = new BehaviorSubject<ContextMenuState<T>>({
    isOpen: false,
    items: [],
    data: null,
    rowIndex: -1,
    position: { x: 0, y: 0 },
  });

  /**
   * Observable state
   */
  public state$: Observable<ContextMenuState<T>> = this.stateSubject.asObservable();

  /**
   * Get current state
   */
  public getState(): ContextMenuState<T> {
    return this.stateSubject.value;
  }

  /**
   * Open context menu at specified position
   *
   * @param config Menu configuration
   */
  public open(config: {
    items: ContextMenuItem<T>[];
    data: T;
    rowIndex: number;
    position: { x: number; y: number };
  }): void {
    // Adjust position to prevent menu from going off-screen
    const adjustedPosition = this.adjustPosition(config.position);

    this.stateSubject.next({
      isOpen: true,
      items: config.items,
      data: config.data,
      rowIndex: config.rowIndex,
      position: adjustedPosition,
    });
  }

  /**
   * Close the context menu
   */
  public close(): void {
    this.stateSubject.next({
      ...this.stateSubject.value,
      isOpen: false,
    });
  }

  /**
   * Check if context menu is currently open
   */
  public isOpen(): boolean {
    return this.stateSubject.value.isOpen;
  }

  /**
   * Adjust menu position to prevent it from going off-screen
   *
   * @param position Original position
   * @returns Adjusted position
   */
  private adjustPosition(position: { x: number; y: number }): { x: number; y: number } {
    const menuWidth = 250; // Estimated menu width
    const menuHeight = 400; // Estimated max menu height

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let adjustedX = position.x;
    let adjustedY = position.y;

    // Adjust X if menu would go off right edge
    if (position.x + menuWidth > viewportWidth) {
      adjustedX = viewportWidth - menuWidth - 10; // 10px margin
    }

    // Adjust Y if menu would go off bottom edge
    if (position.y + menuHeight > viewportHeight) {
      adjustedY = viewportHeight - menuHeight - 10; // 10px margin
    }

    // Ensure minimum position (don't go negative)
    adjustedX = Math.max(10, adjustedX);
    adjustedY = Math.max(10, adjustedY);

    return { x: adjustedX, y: adjustedY };
  }

  /**
   * Evaluate if an item is disabled for the current row data
   *
   * @param item Menu item
   * @param data Row data
   * @returns True if disabled
   */
  public isItemDisabled(item: ContextMenuItem<T>, data: T): boolean {
    if (item.divider) {
      return false;
    }

    if (typeof item.disabled === 'function') {
      return item.disabled(data);
    }

    return !!item.disabled;
  }

  /**
   * Evaluate if an item is hidden for the current row data
   *
   * @param item Menu item
   * @param data Row data
   * @returns True if hidden
   */
  public isItemHidden(item: ContextMenuItem<T>, data: T): boolean {
    if (item.divider) {
      return false;
    }

    if (typeof item.hidden === 'function') {
      return item.hidden(data);
    }

    return !!item.hidden;
  }

  /**
   * Get visible items (excluding hidden items)
   *
   * @param items All items
   * @param data Row data
   * @returns Visible items
   */
  public getVisibleItems(items: ContextMenuItem<T>[], data: T): ContextMenuItem<T>[] {
    return items.filter(item => !this.isItemHidden(item, data));
  }
}
